import { Injectable } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { LoggingService } from '../logging/logging.service';

/**
 * Responsible for all token storage operations.
 * Encapsulates cookie management and provides a clean API for token access.
 *
 * Design principles:
 * - Single Responsibility: Only handles token storage/retrieval
 * - Information Hiding: Cookie implementation details are private
 * - Single Source of Truth: One way to get/set tokens
 */
@Injectable({ providedIn: 'root' })
export class TokenStorageService {
  constructor(
    private readonly cookies: CookieService,
    private readonly log: LoggingService
  ) {}

  /**
   * Get the authentication token from storage.
   * Checks both ws_token (preferred) and token cookies.
   *
   * @returns The token string or null if not found
   */
  getAuthToken(): string | null {
    // First try ws_token (non-HttpOnly, needed for WebSocket)
    const wsToken = this.getCookie('ws_token');
    if (wsToken && wsToken !== 'secure' && wsToken !== 'httponly' && wsToken.length > 20) {
      this.log.debug('TokenStorageService', 'Using ws_token for authentication');
      return wsToken;
    }

    // Try direct document.cookie parsing (handles cross-subdomain scenarios)
    if (!wsToken || wsToken === 'secure' || wsToken === 'httponly') {
      const directWsToken = this.getTokenFromDocumentCookie('ws_token');
      if (directWsToken && directWsToken.length > 20) {
        this.log.info('TokenStorageService', 'Found ws_token via direct document.cookie parsing');
        return directWsToken;
      }
    }

    // Check regular token cookie
    const token = this.getCookie('token');
    if (token && token !== 'secure' && token !== 'httponly') {
      return token;
    }

    // Handle HttpOnly placeholder
    if (token === 'secure' || token === 'httponly') {
      this.log.debug('TokenStorageService', `Token is ${token} placeholder`);
      return token;
    }

    this.log.debug('TokenStorageService', 'No token found in cookies');
    return null;
  }

  /**
   * Store an authentication token.
   *
   * @param token The JWT token to store
   * @param daysToExpire Number of days until cookie expires (default: 7)
   */
  setAuthToken(token: string, daysToExpire: number = 7): void {
    // Only set 'token' cookie if it's not a placeholder value
    if (token && token !== 'httponly' && token !== 'secure') {
      this.setCookie('token', token, daysToExpire);
      this.log.info('TokenStorageService', 'Auth token stored in cookie');
    } else {
      this.log.warn('TokenStorageService', 'Skipping storage of placeholder token');
    }
  }

  /**
   * Clear the authentication token from storage.
   */
  clearAuthToken(): void {
    this.deleteCookie('token');
    this.deleteCookie('ws_token');
    this.log.info('TokenStorageService', 'Auth tokens cleared');
  }

  /**
   * Store user profile information.
   */
  setUserInfo(info: { givenName: string; fullName?: string; email: string; profilePictureUrl: string }): void {
    this.setCookie('givenName', info.givenName, 7);
    if (info.fullName) {
      this.setCookie('fullName', info.fullName, 7);
    }
    this.setCookie('email', info.email, 7);
    this.setCookie('profilePictureUrl', info.profilePictureUrl, 7);
    this.log.debug('TokenStorageService', 'User info stored in cookies');
  }

  /**
   * Get user profile information from storage.
   */
  getUserInfo(): { givenName: string; fullName?: string; email: string; profilePictureUrl: string } {
    return {
      givenName: this.getCookie('givenName'),
      fullName: this.getCookie('fullName') || undefined,
      email: this.getCookie('email'),
      profilePictureUrl: this.getCookie('profilePictureUrl')
    };
  }

  /**
   * Clear all user information from storage.
   */
  clearUserInfo(): void {
    ['givenName', 'fullName', 'email', 'profilePictureUrl'].forEach((c) =>
      this.deleteCookie(c)
    );
    this.log.debug('TokenStorageService', 'User info cleared from cookies');
  }

  /**
   * Clear all authentication data (tokens + user info).
   */
  clearAll(): void {
    this.clearAuthToken();
    this.clearUserInfo();
    this.log.info('TokenStorageService', 'All auth data cleared');
  }

  /**
   * Check if any authentication cookies exist.
   */
  hasAuthCookies(): boolean {
    return !!(this.getCookie('email') || this.getCookie('givenName') || this.getCookie('ws_token') || this.getCookie('token'));
  }

  /**
   * Get profile picture URL from storage.
   */
  getProfilePictureUrl(): string {
    return this.getCookie('profilePictureUrl');
  }

  // ========================================================================
  // PRIVATE METHODS - Cookie Implementation Details
  // ========================================================================

  /**
   * Get a cookie value using ngx-cookie-service.
   */
  private getCookie(name: string): string {
    // First try ngx-cookie-service
    let value = this.cookies.get(name);

    // If not found and we're looking for ws_token or token, try native JavaScript
    // This handles cases where cookies have domain=.finderwise.com
    if (!value && (name === 'ws_token' || name === 'token')) {
      const directValue = this.getTokenFromDocumentCookie(name);
      if (directValue) {
        this.log.debug('TokenStorageService', `Found ${name} via document.cookie parsing`);
        value = directValue;
      }
    }

    return value || '';
  }

  /**
   * Extract token directly from document.cookie (for cross-subdomain scenarios).
   */
  private getTokenFromDocumentCookie(cookieName: string): string | null {
    try {
      const nameEQ = cookieName + "=";
      const cookies = document.cookie.split(';');
      for (const cookie of cookies) {
        let c = cookie.trim();
        if (c.indexOf(nameEQ) === 0) {
          return decodeURIComponent(c.substring(nameEQ.length));
        }
      }
    } catch (e) {
      this.log.error('TokenStorageService', `Error parsing ${cookieName} from document.cookie`, e);
    }
    return null;
  }

  /**
   * Set a cookie with proper security settings.
   */
  private setCookie(name: string, value: string, daysToExpire: number): void {
    // Determine if we're running in a secure context (HTTPS)
    const isSecureContext = window.location.protocol === 'https:' ||
                           window.location.hostname === 'localhost' ||
                           window.location.hostname === '127.0.0.1' ||
                           window.location.hostname.startsWith('192.168.') ||
                           window.location.hostname.startsWith('10.0.0.');

    // Mobile browser cookie persistence fix:
    // - SameSite=Lax: Required for mobile browsers (Chrome 80+, Safari)
    // - Secure flag: Required for HTTPS/production, must be false for HTTP/localhost
    // - Extended session: 7 days to match backend cookie expiration
    this.cookies.set(name, value, daysToExpire, '/', undefined, isSecureContext, 'Lax');
  }

  /**
   * Delete a cookie.
   */
  private deleteCookie(name: string): void {
    this.cookies.delete(name, '/');
  }
}
